import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IMeetingRoomSettingsDays } from 'domain/feature/hub/company/meeting-room/settings/types'
import { getEnabledWeekdayIndexes, isWorkingDay } from './scheduleConfig'
import { AvailabilityCalendarEvent, AvailabilityCalendarView } from './types'

export { addDays, addMonths, isSameDay }

export const SLOT_HEIGHT_PX = 60
export const MONTH_MAX_VISIBLE_EVENTS = 3

const capitalize = (value: string): string =>
  value.length === 0 ? value : `${value.charAt(0).toUpperCase()}${value.slice(1)}`

export const getWeekStart = (date: Date): Date =>
  startOfWeek(date, { weekStartsOn: 1 })

export const formatTime = (hour: number, min = 0): string =>
  `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`

export const formatEventTimeRange = (event: AvailabilityCalendarEvent): string =>
  `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`

export const getEventsForDate = (
  events: AvailabilityCalendarEvent[],
  date: Date
): AvailabilityCalendarEvent[] =>
  events
    .filter(event => isSameDay(event.start, date))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

export const getCurrentTimeIndicatorTop = (
  now: Date,
  hourStart: number,
  hourEnd: number
): number | null => {
  const minutes = now.getHours() * 60 + now.getMinutes()
  const rangeStart = hourStart * 60
  const rangeEnd = hourEnd * 60
  if (minutes < rangeStart || minutes > rangeEnd) return null
  return ((minutes - rangeStart) / 60) * SLOT_HEIGHT_PX
}

export const formatToolbarLabel = (
  view: AvailabilityCalendarView,
  date: Date
): string => {
  if (view === 'month') {
    return capitalize(format(date, 'MMMM yyyy', { locale: ptBR }))
  }

  if (view === 'week') {
    const start = getWeekStart(date)
    const end = addDays(start, 6)
    if (isSameMonth(start, end)) {
      return `${format(start, 'd')} – ${format(end, 'd MMM yyyy', { locale: ptBR })}`
    }
    return `${format(start, 'd MMM', { locale: ptBR })} – ${format(end, 'd MMM yyyy', { locale: ptBR })}`
  }

  return capitalize(format(date, 'EEEE, d MMMM yyyy', { locale: ptBR }))
}

export const formatDayViewSubtitle = (date: Date): string =>
  capitalize(format(date, 'EEEE, MMM yyyy', { locale: ptBR }))

export const formatModalDate = (date: Date): { dayName: string; fullDate: string } => ({
  dayName: capitalize(format(date, 'EEEE', { locale: ptBR })),
  fullDate: format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR }),
})

export const getDayNameShort = (date: Date): string => {
  const label = format(date, 'EEE', { locale: ptBR }).replace('.', '')
  return capitalize(label)
}

export const getEnabledWeekDays = (
  weekStart: Date,
  workingDays: IMeetingRoomSettingsDays
): Date[] =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)).filter(day =>
    isWorkingDay(day, workingDays)
  )

export const getWeekdayHeaders = (workingDays: IMeetingRoomSettingsDays): string[] => {
  const start = getWeekStart(new Date())
  return getEnabledWeekDays(start, workingDays).map(getDayNameShort)
}

export const buildMonthCells = (
  currentDate: Date,
  workingDays: IMeetingRoomSettingsDays
): Array<{ date: Date; inCurrentMonth: boolean }> => {
  const enabledDow = new Set(getEnabledWeekdayIndexes(workingDays))
  const gridStart = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 })

  return eachDayOfInterval({ start: gridStart, end: gridEnd })
    .filter(date => enabledDow.has(date.getDay()))
    .map(date => ({
      date,
      inCurrentMonth: isSameMonth(date, currentDate),
    }))
}

export const hourSlots = (hourStart: number, hourEnd: number): number[] => {
  const hours: number[] = []
  for (let hour = hourStart; hour <= hourEnd; hour += 1) {
    hours.push(hour)
  }
  return hours
}

export const shiftToWorkingDay = (
  date: Date,
  direction: -1 | 1,
  workingDays: IMeetingRoomSettingsDays
): Date => {
  let next = addDays(date, direction)
  for (let i = 0; i < 14; i += 1) {
    if (isWorkingDay(next, workingDays)) return next
    next = addDays(next, direction)
  }
  return date
}

export const ensureWorkingDay = (
  date: Date,
  workingDays: IMeetingRoomSettingsDays
): Date => {
  if (isWorkingDay(date, workingDays)) return date
  return shiftToWorkingDay(date, 1, workingDays)
}
